// Escritório (0.45): grupo de trabalho com equipe, conversa, documentos, reuniões de vídeo e gravações.
// Usa os grupos com convite (aba "escritorio") e a conversa do grupo; documentos e reuniões ficam em tabelas próprias.
import { supabase } from './supabase';
import { DEMO } from './env';
import { t } from './i18n';
import { fusoAparelho } from './fuso';
import { criarGrupo, recarregarGrupos, type MeuGrupo } from './grupos';

export type DocEsc = { id: string; grupo_id: string; path: string; nome: string; mime: string; tam: number; tipo: 'arquivo' | 'gravacao';
  visivel: boolean; status: 'pronto' | 'gravando' | 'processando'; enviado_por: string | null; enviado_nome: string; criado_em: string;
  pasta?: string; fixado?: boolean };   // texto 27 (B7)
export type Reuniao = { id: string; grupo_id: string; titulo: string; data: string; hora: string; fuso: string; duracao: number;
  duracao_real?: number | null;   // texto 24 (4c): quanto a chamada durou de verdade (o servidor guarda ao sair da chamada)
  participantes?: string[] | null; repetir?: string; link?: string; ata?: string;   // texto 27 (B6)
  descricao: string; criada_por: string | null; criada_nome: string; criado_em: string };

export const ehEscritorio = (g: Pick<MeuGrupo, 'aba'>) => g.aba === 'escritorio';

const erroAmigavel = (e: any) => {
  const m = String(e?.message ?? e);
  if (/escritorio_|does not exist|schema cache|Bucket not found/i.test(m))
    return new Error(/escritorio_(tarefa|mural|membro|reuniao_(extra|resp|ata)|doc_(visto|coment|organizar)|passar)|participantes|fixado|pasta/i.test(m)
      ? t('Esta parte do Escritório ainda não foi ligada no servidor. No computador, rode o 56-SERVIDOR-0.96.bat (uma vez só).')
      : t('O Escritório ainda não foi ligado no servidor. No computador, rode o 29-SERVIDOR-0.45.bat (uma vez só).'));
  if (/exceeded the maximum allowed size|Payload too large|413/i.test(m)) return new Error(t('O arquivo passa de 50 MB.'));
  return e instanceof Error ? e : new Error(m);
};
async function sb<T>(p: PromiseLike<{ data: T; error: any }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw erroAmigavel(error);
  return data;
}

// ─── Demonstração ───
const demo = { docs: [] as DocEsc[], reunioes: [] as Reuniao[], tarefas: [] as Tarefa[], mural: [] as Aviso[], info: [] as MembroInfo[],
  resp: [] as RespReuniao[], docVistos: [] as (Visto & { doc_id: string })[], coment: [] as Comentario[] };
const agora = () => new Date().toISOString();

export async function criarEscritorio(nome: string): Promise<{ id: string; codigo: string; conversa: string }> {
  if (DEMO) {
    const g = await criarGrupo('escritorio', nome);
    const amanha = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
    demo.reunioes.push({ id: 'r' + Date.now(), grupo_id: g.id, titulo: t('Reunião de equipe'), data: amanha, hora: '09:00', fuso: 'America/Sao_Paulo', duracao: 60,
      descricao: '', criada_por: 'eu', criada_nome: 'Demonstração', criado_em: agora() });
    demo.docs.push({ id: 'd' + Date.now(), grupo_id: g.id, path: g.id + '/contrato.pdf', nome: 'Contrato de prestação de serviços.pdf', mime: 'application/pdf', tam: 284000,
      tipo: 'arquivo', visivel: true, status: 'pronto', enviado_por: 'eu', enviado_nome: 'Demonstração', criado_em: agora() });
    await recarregarGrupos();
    return { id: g.id, codigo: g.codigo, conversa: 'turma' };
  }
  const r = await sb(supabase!.rpc('escritorio_criar', { p_nome: nome }));
  await recarregarGrupos();
  return r as any;
}

export async function adicionarNaEquipe(grupo: string, user: string) {
  if (DEMO) return;
  await sb(supabase!.rpc('escritorio_adicionar', { p_grupo: grupo, p_user: user }));
}

// ─── Documentos ───
export async function docsDe(grupo: string): Promise<DocEsc[]> {
  if (DEMO) return demo.docs.filter(d => d.grupo_id === grupo);
  return sb(supabase!.from('escritorio_docs').select('*').eq('grupo_id', grupo).order('criado_em', { ascending: false })) as Promise<DocEsc[]>;
}

const uid = () => Math.random().toString(36).slice(2, 10);
export async function enviarDoc(grupo: string, arq: { bytes: Uint8Array; mime: string; nome: string }) {
  if (arq.bytes.length > 50 * 1024 * 1024) throw new Error(t('O arquivo passa de 50 MB.'));
  const limpo = arq.nome.replace(/[^\w.\-]+/g, '_').slice(-80) || 'arquivo';
  const path = `${grupo}/${Date.now().toString(36)}-${uid()}-${limpo}`;
  if (DEMO) {
    demo.docs.unshift({ id: 'd' + Date.now(), grupo_id: grupo, path, nome: arq.nome, mime: arq.mime, tam: arq.bytes.length, tipo: 'arquivo', visivel: true,
      status: 'pronto', enviado_por: 'eu', enviado_nome: 'Demonstração', criado_em: agora() });
    return;
  }
  const up = await supabase!.storage.from('escritorio').upload(path, arq.bytes, { contentType: arq.mime || 'application/octet-stream', upsert: false });
  if (up.error) throw erroAmigavel(up.error);
  try { await sb(supabase!.rpc('escritorio_doc_novo', { p_grupo: grupo, p_path: path, p_nome: arq.nome, p_mime: arq.mime, p_tam: arq.bytes.length })); }
  catch (e) { await supabase!.storage.from('escritorio').remove([path]).catch(() => {}); throw e; }
}

/** Endereço temporário (1 hora) para abrir ou baixar. Gravação recém-terminada pode levar alguns minutos para ficar pronta. */
export async function urlDoDoc(d: DocEsc): Promise<string> {
  if (DEMO) throw new Error(t('Na prévia de demonstração os arquivos não abrem.'));
  if (d.status === 'gravando') throw new Error(t('A reunião ainda está sendo gravada.'));
  const { data, error } = await supabase!.storage.from('escritorio').createSignedUrl(d.path, 3600, { download: d.nome });
  if (error || !data?.signedUrl) {
    if (d.tipo === 'gravacao') throw new Error(t('A gravação ainda está sendo preparada. Tente de novo em alguns minutos.'));
    throw erroAmigavel(error ?? new Error(t('Arquivo não encontrado.')));
  }
  if (d.status === 'processando') supabase!.rpc('escritorio_doc_mudar', { p_doc: d.id }).then(() => {}, () => {});
  return data.signedUrl;
}

export async function mudarDoc(d: DocEsc, p: { nome?: string; visivel?: boolean }) {
  if (DEMO) { const x = demo.docs.find(y => y.id === d.id); if (x) Object.assign(x, p.nome ? { nome: p.nome } : {}, p.visivel !== undefined ? { visivel: p.visivel } : {}); return; }
  await sb(supabase!.rpc('escritorio_doc_mudar', { p_doc: d.id, p_nome: p.nome ?? null, p_visivel: p.visivel ?? null }));
}

export async function apagarDoc(d: DocEsc) {
  if (DEMO) { demo.docs = demo.docs.filter(x => x.id !== d.id); return; }
  const path = await sb(supabase!.rpc('escritorio_doc_apagar', { p_doc: d.id }));
  if (path) await supabase!.storage.from('escritorio').remove([path as string]).catch(() => {});
}

// ─── Reuniões ───
export async function reunioesDe(grupo: string): Promise<Reuniao[]> {
  if (DEMO) return demo.reunioes.filter(r => r.grupo_id === grupo);
  return sb(supabase!.from('escritorio_reunioes').select('*').eq('grupo_id', grupo).order('data').order('hora')) as Promise<Reuniao[]>;
}
/** Marca a reunião. Texto 27 (B6): quem participa (vazio = a equipe toda), repetir toda semana e link externo vão logo depois. */
export async function marcarReuniao(grupo: string, r: { titulo: string; data: string; hora: string; duracao: number; descricao: string },
  extra?: { participantes: string[]; repetir: string; link: string }) {
  if (DEMO) { demo.reunioes.push({ id: 'r' + Date.now(), grupo_id: grupo, ...r, ...(extra ?? {}), fuso: fusoAparelho(), criada_por: 'eu', criada_nome: 'Demonstração', criado_em: agora() }); return; }
  const id = await sb(supabase!.rpc('escritorio_reuniao_criar', { p_grupo: grupo, p_titulo: r.titulo, p_data: r.data, p_hora: r.hora, p_fuso: fusoAparelho(),
    p_duracao: r.duracao, p_descricao: r.descricao }));
  if (extra && (extra.participantes.length || extra.repetir || extra.link) && id)
    await sb(supabase!.rpc('escritorio_reuniao_extra', { p_reuniao: id, p_participantes: extra.participantes, p_repetir: extra.repetir, p_link: extra.link }));
}
export type RespReuniao = { reuniao_id: string; user_id: string; nome: string; resposta: 'vou' | 'naovou' };
export async function respostasDe(ids: string[]): Promise<RespReuniao[]> {
  if (!ids.length) return [];
  if (DEMO) return demo.resp.filter(r => ids.includes(r.reuniao_id));
  return sb(supabase!.from('escritorio_reuniao_resp').select('reuniao_id,user_id,nome,resposta').in('reuniao_id', ids)) as Promise<RespReuniao[]>;
}
export async function responderReuniao(id: string, resposta: 'vou' | 'naovou') {
  if (DEMO) { demo.resp = demo.resp.filter(r => !(r.reuniao_id === id && r.user_id === 'eu')); demo.resp.push({ reuniao_id: id, user_id: 'eu', nome: 'Demonstração', resposta }); return; }
  await sb(supabase!.rpc('escritorio_reuniao_responder', { p_reuniao: id, p_resposta: resposta }));
}
export async function salvarAta(id: string, ata: string) {
  if (DEMO) { const r = demo.reunioes.find(x => x.id === id); if (r) r.ata = ata; return; }
  await sb(supabase!.rpc('escritorio_reuniao_ata', { p_reuniao: id, p_ata: ata }));
}
/** Reunião que se repete toda semana: a próxima vez a partir de hoje (no mesmo dia da semana). */
export function proximaVez(data: string, hoje: string): string {
  if (data >= hoje) return data;
  const d0 = new Date(data + 'T12:00:00'), h = new Date(hoje + 'T12:00:00');
  const dias = Math.round((h.getTime() - d0.getTime()) / 864e5);
  const n = Math.ceil(dias / 7) * 7;
  const r = new Date(d0.getTime() + n * 864e5);
  return `${r.getFullYear()}-${String(r.getMonth() + 1).padStart(2, '0')}-${String(r.getDate()).padStart(2, '0')}`;
}
export async function cancelarReuniao(id: string) {
  if (DEMO) { demo.reunioes = demo.reunioes.filter(r => r.id !== id); return; }
  await sb(supabase!.rpc('escritorio_reuniao_apagar', { p_reuniao: id }));
}

// ─── Texto 27 (B5): papel e cargo de cada membro, mural de avisos e tarefas da equipe ───
export type Papel = 'edita' | 've';
export type MembroInfo = { grupo_id: string; user_id: string; cargo: string; papel: Papel };
export async function infoMembros(grupo: string): Promise<MembroInfo[]> {
  if (DEMO) return demo.info.filter(i => i.grupo_id === grupo);
  try { return await sb(supabase!.from('escritorio_membro_info').select('*').eq('grupo_id', grupo)) as MembroInfo[]; }
  catch { return []; }   // servidor ainda sem a 0.96: todos editam, sem cargo
}
export async function definirMembro(grupo: string, user: string, cargo: string, papel: Papel) {
  if (DEMO) { demo.info = demo.info.filter(i => !(i.grupo_id === grupo && i.user_id === user)); demo.info.push({ grupo_id: grupo, user_id: user, cargo, papel }); return; }
  await sb(supabase!.rpc('escritorio_membro_definir', { p_grupo: grupo, p_user: user, p_cargo: cargo, p_papel: papel }));
}
export async function passarAdmin(grupo: string, user: string) {
  if (DEMO) return;
  await sb(supabase!.rpc('escritorio_passar_admin', { p_grupo: grupo, p_user: user }));
  await recarregarGrupos();
}

export type Visto = { user_id: string; nome: string; em: string };
export type Aviso = { id: string; grupo_id: string; texto: string; autor: string | null; autor_nome: string; criado_em: string; vistos: Visto[] };
export async function muralDe(grupo: string): Promise<Aviso[]> {
  if (DEMO) return demo.mural.filter(a => a.grupo_id === grupo);
  const r = await sb(supabase!.from('escritorio_mural').select('*, vistos:escritorio_mural_vistos(user_id,nome,em)').eq('grupo_id', grupo).order('criado_em', { ascending: false }));
  return (r as any[]).map(a => ({ ...a, vistos: a.vistos ?? [] }));
}
export async function postarAviso(grupo: string, texto: string) {
  if (DEMO) { demo.mural.unshift({ id: 'a' + Date.now(), grupo_id: grupo, texto, autor: 'eu', autor_nome: 'Demonstração', criado_em: agora(), vistos: [{ user_id: 'eu', nome: 'Demonstração', em: agora() }] }); return; }
  await sb(supabase!.rpc('escritorio_mural_postar', { p_grupo: grupo, p_texto: texto }));
}
export async function avisoVisto(id: string) {
  if (DEMO) return;
  await supabase!.rpc('escritorio_mural_visto', { p_aviso: id }).then(() => {}, () => {});
}
export async function apagarAviso(id: string) {
  if (DEMO) { demo.mural = demo.mural.filter(a => a.id !== id); return; }
  await sb(supabase!.rpc('escritorio_mural_apagar', { p_aviso: id }));
}

export type SitTarefa = 'afazer' | 'fazendo' | 'feito';
export type Tarefa = { id: string; grupo_id: string; titulo: string; cliente: string; prazo: string | null; situacao: SitTarefa;
  responsavel: string | null; responsavel_nome: string; reuniao_id: string | null; criada_por: string | null; criada_nome: string; criado_em: string; feita_em: string | null };
export async function tarefasDe(grupo: string): Promise<Tarefa[]> {
  if (DEMO) return demo.tarefas.filter(x => x.grupo_id === grupo);
  return sb(supabase!.from('escritorio_tarefas').select('*').eq('grupo_id', grupo).order('criado_em', { ascending: false })) as Promise<Tarefa[]>;
}
export async function salvarTarefa(grupo: string, x: { id?: string; titulo: string; cliente: string; prazo: string | null; responsavel?: string | null; reuniao?: string | null }) {
  if (DEMO) {
    const v = demo.tarefas.find(y => y.id === x.id);
    if (v) Object.assign(v, { titulo: x.titulo, cliente: x.cliente, prazo: x.prazo });
    else demo.tarefas.unshift({ id: 't' + Date.now(), grupo_id: grupo, titulo: x.titulo, cliente: x.cliente, prazo: x.prazo, situacao: 'afazer',
      responsavel: x.responsavel ?? null, responsavel_nome: x.responsavel ? 'Demonstração' : '', reuniao_id: x.reuniao ?? null, criada_por: 'eu', criada_nome: 'Demonstração', criado_em: agora(), feita_em: null });
    return;
  }
  await sb(supabase!.rpc('escritorio_tarefa_salvar', { p_grupo: grupo, p_id: x.id ?? null, p_titulo: x.titulo, p_cliente: x.cliente, p_prazo: x.prazo,
    p_responsavel: x.responsavel ?? null, p_reuniao: x.reuniao ?? null }));
}
export async function euFaco(x: Tarefa) {
  if (DEMO) { Object.assign(x, { responsavel: 'eu', responsavel_nome: 'Demonstração' }); return; }
  await sb(supabase!.rpc('escritorio_tarefa_eu_faco', { p_id: x.id }));
}
export async function mudarSituacao(x: Tarefa, s: SitTarefa) {
  if (DEMO) { Object.assign(x, { situacao: s, feita_em: s === 'feito' ? agora() : null }); return; }
  await sb(supabase!.rpc('escritorio_tarefa_situacao', { p_id: x.id, p_situacao: s }));
}
export async function apagarTarefa(x: Tarefa) {
  if (DEMO) { demo.tarefas = demo.tarefas.filter(y => y.id !== x.id); return; }
  await sb(supabase!.rpc('escritorio_tarefa_apagar', { p_id: x.id }));
}

// ─── Texto 27 (B7): documentos internos: visto por, comentários, pasta e fixar ───
export type Comentario = { id: string; doc_id: string; autor: string | null; nome: string; texto: string; em: string };
export async function vistosDosDocs(ids: string[]): Promise<(Visto & { doc_id: string })[]> {
  if (!ids.length) return [];
  if (DEMO) return demo.docVistos.filter(v => ids.includes(v.doc_id));
  try { return await sb(supabase!.from('escritorio_doc_vistos').select('doc_id,user_id,nome,em').in('doc_id', ids)) as any; } catch { return []; }
}
export async function docVisto(d: DocEsc) {
  if (DEMO) { if (!demo.docVistos.some(v => v.doc_id === d.id && v.user_id === 'eu')) demo.docVistos.push({ doc_id: d.id, user_id: 'eu', nome: 'Demonstração', em: agora() }); return; }
  await supabase!.rpc('escritorio_doc_visto', { p_doc: d.id }).then(() => {}, () => {});
}
export async function comentariosDe(doc: string): Promise<Comentario[]> {
  if (DEMO) return demo.coment.filter(c => c.doc_id === doc);
  return sb(supabase!.from('escritorio_doc_coment').select('*').eq('doc_id', doc).order('em')) as Promise<Comentario[]>;
}
export async function comentar(doc: string, texto: string) {
  if (DEMO) { demo.coment.push({ id: 'c' + Date.now(), doc_id: doc, autor: 'eu', nome: 'Demonstração', texto, em: agora() }); return; }
  await sb(supabase!.rpc('escritorio_doc_comentar', { p_doc: doc, p_texto: texto }));
}
export async function organizarDoc(d: DocEsc, p: { pasta?: string; fixado?: boolean }) {
  if (DEMO) { const x = demo.docs.find(y => y.id === d.id); if (x) Object.assign(x, p); return; }
  await sb(supabase!.rpc('escritorio_doc_organizar', { p_doc: d.id, p_pasta: p.pasta ?? null, p_fixado: p.fixado ?? null }));
}

export const tamanho = (b: number) => (b >= 1048576 ? (b / 1048576).toFixed(1).replace('.', ',') + ' MB' : b >= 1024 ? Math.round(b / 1024) + ' KB' : b + ' B');
export const iconeDoc = (d: Pick<DocEsc, 'mime' | 'tipo' | 'nome'>) =>
  d.tipo === 'gravacao' ? (d.mime.startsWith('audio') ? '🎙️' : '🎥') : d.mime.startsWith('image/') ? '🖼️' : /pdf/.test(d.mime) ? '📕'
    : /sheet|excel|csv/.test(d.mime) || /\.(xlsx?|csv)$/i.test(d.nome) ? '📊' : /word|document/.test(d.mime) || /\.docx?$/i.test(d.nome) ? '📝'
      : d.mime.startsWith('video/') ? '🎬' : d.mime.startsWith('audio/') ? '🎵' : '📄';
